import React from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { Clock } from 'lucide-react';
import { useLogoutContext } from '../providers/logout-context';
import { endServerSession, sendSessionHeartbeat } from '../util/session';

// Security requirement: a signed-in session ends after 15 minutes with no user
// activity. A warning with a countdown shows for the last minute so someone
// mid-read isn't thrown out without notice.
const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARNING_MS = 60 * 1000;
const CHECK_INTERVAL_MS = 1000;
// Activity is written at most this often - mousemove fires constantly.
const ACTIVITY_WRITE_THROTTLE_MS = 5000;
// The server enforces the same 15-minute limit on its own session
// (express-server session-service.js), counting API requests - so while the
// user is active, a heartbeat goes out at most this often, keeping a long read
// with no other API calls from expiring the server side under them.
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// Shared through localStorage so activity in ANY open tab keeps every tab
// signed in (and a sign-out in one ends all of them - AuthGuard notices the
// cookies are gone within a second).
const LAST_ACTIVITY_KEY = 'fa:lastActivity';
export const IDLE_LOGOUT_FLAG_KEY = 'fa:idleLogout';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// Public pages where nobody is signed in to the app itself.
const isPublicPath = (path: string) =>
    ['/login', '/signup', '/forgot-password', '/reset-password', '/terms-and-conditions', '/admin', '/admin/forgot-password'].includes(path)
    || path.startsWith('/view/');

const readLastActivity = (): number => {
    try {
        const v = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
        return Number.isFinite(v) && v > 0 ? v : Date.now();
    } catch {
        return Date.now();
    }
};

const writeLastActivity = (t: number) => {
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(t)); } catch { /* storage blocked - this tab still tracks its own */ }
};

const isSignedIn = () => !!Cookies.get('token') && !!Cookies.get('user');

const IdleTimeout: React.FC = () => {
    const location = useLocation();
    const { initiateLogout } = useLogoutContext();
    const [remainingMs, setRemainingMs] = React.useState<number | null>(null);
    // Fallback when localStorage is unavailable (private mode etc.).
    const localLastActivity = React.useRef(Date.now());
    const lastWrite = React.useRef(0);
    const warningOpen = remainingMs !== null;
    const warningOpenRef = React.useRef(false);
    warningOpenRef.current = warningOpen;

    const active = !isPublicPath(location.pathname);

    const markActive = React.useCallback((force = false) => {
        const now = Date.now();
        localLastActivity.current = now;
        if (force || now - lastWrite.current >= ACTIVITY_WRITE_THROTTLE_MS) {
            lastWrite.current = now;
            writeLastActivity(now);
        }
    }, []);

    const lastHeartbeat = React.useRef(Date.now());
    const heartbeatInFlight = React.useRef(false);

    const signOut = React.useCallback(() => {
        try { localStorage.setItem(IDLE_LOGOUT_FLAG_KEY, '1'); } catch { /* best-effort message only */ }
        initiateLogout();
        // Before the cookie goes - it carries the token the server needs.
        endServerSession();
        Cookies.remove('token');
        Cookies.remove('user');
        Cookies.remove('entities');
        window.location.href = '/login';
    }, [initiateLogout]);

    React.useEffect(() => {
        if (!active) return;
        // Starting a fresh signed-in view counts as activity.
        markActive(true);

        // While the warning is up, only the explicit "Stay signed in" click
        // keeps the session - stray mouse movement shouldn't silently dismiss it.
        const onActivity = () => { if (!warningOpenRef.current) markActive(); };
        ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true, capture: true }));

        const timer = window.setInterval(() => {
            if (!isSignedIn()) { setRemainingMs(null); return; }
            const last = Math.max(readLastActivity(), localLastActivity.current);
            const idleFor = Date.now() - last;

            // Active since the last heartbeat and it's been a while - keep the
            // server-side session in step. A 401 means the server already
            // ended it (e.g. expired while the laptop slept), so sign out.
            if (!heartbeatInFlight.current && last > lastHeartbeat.current
                && Date.now() - lastHeartbeat.current >= HEARTBEAT_INTERVAL_MS) {
                heartbeatInFlight.current = true;
                lastHeartbeat.current = Date.now();
                sendSessionHeartbeat()
                    .then(alive => { if (!alive) signOut(); })
                    .finally(() => { heartbeatInFlight.current = false; });
            }

            if (idleFor >= IDLE_LIMIT_MS) {
                signOut();
            } else if (idleFor >= IDLE_LIMIT_MS - WARNING_MS) {
                setRemainingMs(IDLE_LIMIT_MS - idleFor);
            } else {
                // Covers activity in another tab closing this tab's warning.
                setRemainingMs(null);
            }
        }, CHECK_INTERVAL_MS);

        return () => {
            ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity, { capture: true } as EventListenerOptions));
            window.clearInterval(timer);
        };
    }, [active, markActive, signOut]);

    if (!active || !warningOpen) return null;

    const seconds = Math.max(0, Math.ceil((remainingMs ?? 0) / 1000));

    return (
        <Dialog open maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                <Clock size={40} color="var(--color-primary)" />
                <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 1.5 }}>Are you still there?</Typography>
                <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 14, mt: 1 }}>
                    For your security, you'll be signed out in <strong>{seconds}s</strong> due to inactivity.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={signOut}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                    >
                        Sign out
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => { markActive(true); setRemainingMs(null); }}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', background: 'var(--color-primary)', '&:hover': { background: 'var(--color-primary-hover)', boxShadow: 'none' } }}
                    >
                        Stay signed in
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default IdleTimeout;
